export interface SingleLineToken {
    text: string;
    hasSpaceAfter: boolean;
}

export interface MultiLineToken {
    text: string;
    trailingWhitespace: string;
}

export interface ResultAndSpaces {
    newText: string;
    trimmedWhitespace: string;
}

/*
 * Matches whitespaces excluding the full-width space character (U+3000). This is because we need
 * to preserve this character for certain languages, such as Japanese. All other types of
 * whitespace are typically normalized into the standard space character.
 */
const FULL_WIDTH_SPACE = '\\u3000';
const SINGLE_WHITESPACE = `[^\\S${FULL_WIDTH_SPACE}]`;
export const SINGLE_WHITESPACE_REGEX = new RegExp(`${SINGLE_WHITESPACE}`, 'g');
export const MULTIPLE_WHITESPACE_REGEX = new RegExp(
    `${SINGLE_WHITESPACE}+`,
    'g',
);
const MULTIPLE_WHITESPACE_AT_START_REGEX = new RegExp(
    `^${SINGLE_WHITESPACE}+`,
    'g',
);
const MULTIPLE_WHITESPACE_AT_END_REGEX = new RegExp(
    `${SINGLE_WHITESPACE}+$`,
    'g',
);
const FIRST_WORD_AND_REMAINING_TEXT_REGEX = new RegExp(
    `^([\\S${FULL_WIDTH_SPACE}]*)(${SINGLE_WHITESPACE}+)([\\S\\s${FULL_WIDTH_SPACE}]*)`,
);

export function trimStartWhitespace(text: string): ResultAndSpaces {
    const newText = text.replace(MULTIPLE_WHITESPACE_AT_START_REGEX, '');
    return {
        newText,
        trimmedWhitespace: text.substring(0, text.length - newText.length),
    };
}

export function trimEndWhitespace(text: string): ResultAndSpaces {
    const newText = text.replace(MULTIPLE_WHITESPACE_AT_END_REGEX, '');
    return {
        newText,
        trimmedWhitespace: text.substring(newText.length),
    };
}

interface FirstWordAndRemainingText {
    firstWord: string;
    remainingText: string;
}

// Note: this will return an empty string as the first word if there is any leading whitespace
function extractFirstWord(text: string): FirstWordAndRemainingText {
    // Find the first whitespace and split the text where it is found. If a match is found, it will
    // be an array containing 4 items: [
    //     the full text (same as the input),
    //     the first word,
    //     the whitespace between the first word and the rest of the text,
    //     the rest of the text after that whitespace (including spanning newlines)
    // ].
    const match = FIRST_WORD_AND_REMAINING_TEXT_REGEX.exec(text);
    if (match) {
        return {
            firstWord: match[1],
            remainingText: match[2] + match[3],
        };
    }

    // There was no whitespace found, so it must be some text with no whitespace
    return {
        firstWord: text,
        remainingText: '',
    };
}

/*
 * Helper function for matchTextToTokens. This function takes the current text and set of old
 * tokens, then matches as many of the old tokens as possible inside the text. It scans from the
 * left and from the right, with a flag to control which side it searches from first. Note that the
 * function will always scan from both directions during a single call - the flag simply controls
 * which direction we scan FIRST. It returns any remaining unmatched text as a flat string, without
 * creating any new unrecognized tokens out of it (that is done in the parent function).
 */
function getMatchedTokensAndRemainingText<
    Token extends SingleLineToken | MultiLineToken
>(
    text: string,
    oldTokens: Token[],
    supportsMultiLine: boolean,
    scanFromLeftFirst: boolean,
) {
    // We don't support leading whitespace at the start of the text
    let remainingText = text.trimStart();

    const prefixTokens: Token[] = [];
    const suffixTokens: Token[] = [];

    // Helper to scan from left to right
    const scanFromLeft = (limitIdx = oldTokens.length) => {
        let lastMatchedTokenIdx = -1;
        for (let tokenIdx = 0; tokenIdx < limitIdx; tokenIdx++) {
            const currentToken: Token = oldTokens[tokenIdx];

            // Skip any empty tokens
            if (currentToken.text.length === 0) {
                continue;
            }

            // Check if the current token can be found at the start of the text
            if (!remainingText.startsWith(currentToken.text)) {
                // We have encountered unmatchable text, so stop scanning
                break;
            }

            // We have matched the current token in the text
            lastMatchedTokenIdx = tokenIdx;

            // Shallow copy of the current token
            const newToken: Token = {
                ...currentToken,
            };

            // Remove the token text from the start of the remaining text
            remainingText = remainingText.substring(currentToken.text.length);

            // Get the chunk of whitespace after our current token
            const { newText, trimmedWhitespace } = trimStartWhitespace(
                remainingText,
            );
            remainingText = newText;

            // Update the appropriate field to record the trailing whitespaces
            if (supportsMultiLine) {
                (newToken as MultiLineToken).trailingWhitespace = trimmedWhitespace;
            } else {
                (newToken as SingleLineToken).hasSpaceAfter =
                    trimmedWhitespace.length > 0;
                newToken.text += trimmedWhitespace.slice(
                    0,
                    trimmedWhitespace.length - 1,
                );
            }

            prefixTokens.push(newToken);
        }

        return lastMatchedTokenIdx;
    };

    // Helper to scan from right to left
    const scanFromRight = (limitIdx = -1) => {
        let lastMatchedTokenIdx = oldTokens.length;
        for (
            let tokenIdx = oldTokens.length - 1;
            tokenIdx > limitIdx;
            tokenIdx--
        ) {
            const currentToken: Token = oldTokens[tokenIdx];

            // Skip any empty tokens
            if (currentToken.text.length === 0) {
                continue;
            }

            // Get the chunk of whitespace that would be at the end of our current token. We don't
            // update the remaining text yet, since we want to preserve this whitespace if we can't
            // match the current token. If there is a match, we will remove the whitespace from the
            // remaining text at that point.
            const { newText, trimmedWhitespace } = trimEndWhitespace(
                remainingText,
            );

            // Check if the current token can be found at the end of the text
            if (!newText.endsWith(currentToken.text)) {
                // We have encountered unmatchable text, so stop scanning (and leave trailing
                // whitespace intact)
                break;
            }

            // We have matched the current token in the text
            lastMatchedTokenIdx = tokenIdx;

            // Remove the trailing whitespace from the end of the remaining text
            remainingText = newText;

            // Shallow copy of the current token
            const newToken: Token = {
                ...currentToken,
            };

            // Remove the token text from the end of the remaining text
            remainingText = remainingText.substring(
                0,
                remainingText.length - currentToken.text.length,
            );

            // Update the appropriate field to record the trailing whitespaces
            if (supportsMultiLine) {
                (newToken as MultiLineToken).trailingWhitespace = trimmedWhitespace;
            } else {
                (newToken as SingleLineToken).hasSpaceAfter =
                    trimmedWhitespace.length > 0;
                newToken.text += trimmedWhitespace.slice(
                    0,
                    trimmedWhitespace.length - 1,
                );
            }

            // Add at start of list to preserve normal order, since we're going from right to left
            suffixTokens.unshift(newToken);
        }

        return lastMatchedTokenIdx;
    };

    // Scan from each direction, using the passed flag to decide which side to scan from first
    if (scanFromLeftFirst) {
        const limitIdx = scanFromLeft();
        scanFromRight(limitIdx);
    } else {
        const limitIdx = scanFromRight();
        scanFromLeft(limitIdx);
    }

    return {
        remainingText,
        prefixTokens,
        suffixTokens,
    };
}

/*
 * Given text and a set of old tokens, parses the text to match it against the old tokens, and
 * creates new tokens along the way for any unmatched text. When creating new tokens out of
 * unmatched text, we will create tokens based on whitespace boundaries (ie. one token per "word").
 * All whitespace at the end of a token and before the next non-whitespace character will be
 * considered part of that token. Any extra fields contained within the token objects that are not
 * part of the token interface will be preserved if the tokens are carried over into the returned
 * list, however any newly-constructed tokens (for unmatched text) will naturally not contain any
 * of those fields.
 *
 * Matching of tokens to the text is done as follows:
 * 1. Using the getMatchedTokensAndRemainingText helper function, we try to match as many of the
 *    old tokens as we can against the current text. We typically call this helper function twice,
 *    once scanning from left to right first, and the second time scanning from right to left
 *    first. This is because it is possible we will get more matched text when scanning from one
 *    direction first compared to the other. We use the scan which give us the most matched text.
 * 2. If there is still text content that was not matched in step 1, we break it up at all
 *    whitespaces, and create a new unmatched token for each word.
 *
 * Corner cases:
 * - If we are able to match the text of a token in the string, but there is more text immediately
 *   before or after it with no whitespace separation, we will consider all of that text as being
 *   part of the token. The token will be treated as a new unmatched token, so any extra fields
 *   from the original tokens will not be preserved.
 * - Currently there is no support for whitespace at the very start of the text (this will be
 *   trimmed away). Whitespace at the very end is supported however (owned by the last token).
 * - If the text is a blank string, an empty array will be returned. We do not support matching an
 *   empty string with an "empty" token which also contains no text content.
 * - Undefined behavior for tokens that have leading or traling whitespace in the token text, or if
 *   the token text contains only whitespace, or is an empty string.
 * - Undefined behavior for tokens that match both SingleLineToken and MultiLineToken interfaces.
 */
export function matchTextToTokens<
    Token extends SingleLineToken | MultiLineToken
>(text: string, oldTokens: Token[], supportsMultiLine: boolean): Token[] {
    let remainingText: string;
    let prefixTokens: Token[];
    let suffixTokens: Token[];

    // Match as many oldTokens as we can against the text. We perform a scan where we begin from
    // the left side, and another scan where we begin from the right side. It is possible the
    // number of tokens we are able to match will differ between the two scans [SCAL-144862], in
    // which case we take the results from whichever scan resulted in less remaining unmatched
    // text. We also have an optimization where we skip the second scan if the first scan already
    // resulted in no unmatched text.
    const resultsScanningFromLeftFirst = getMatchedTokensAndRemainingText(
        text,
        oldTokens,
        supportsMultiLine,
        true, // Scan from left first
    );
    if (resultsScanningFromLeftFirst.remainingText.length === 0) {
        // The first scan already matched all text, so just use that result
        ({
            remainingText,
            prefixTokens,
            suffixTokens,
        } = resultsScanningFromLeftFirst);
    } else {
        const resultsScanningFromRightFirst = getMatchedTokensAndRemainingText(
            text,
            oldTokens,
            supportsMultiLine,
            false, // Scan from right first
        );
        if (
            resultsScanningFromLeftFirst.remainingText.length <=
            resultsScanningFromRightFirst.remainingText.length
        ) {
            // The scan starting from the left resulted in less unmatched text, so use that result
            ({
                remainingText,
                prefixTokens,
                suffixTokens,
            } = resultsScanningFromLeftFirst);
        } else {
            // The scan starting from the right resulted in less unmatched text, so use that result
            ({
                remainingText,
                prefixTokens,
                suffixTokens,
            } = resultsScanningFromRightFirst);
        }
    }

    // If there was any unmatched text, create new tokens out of it by splitting on whitespaces
    const middleTokens: Token[] = [];
    while (remainingText.length > 0) {
        // Get the first word from the remaining text (updating the remaining text in the process)
        let firstWord;
        ({ firstWord, remainingText } = extractFirstWord(remainingText));

        // Create a new token
        const newToken = {
            text: firstWord,
        };

        // Get the chunk of whitespace after our current token
        const { newText, trimmedWhitespace } = trimStartWhitespace(
            remainingText,
        );
        remainingText = newText;

        // Set the appropriate field to record the trailing whitespaces
        if (supportsMultiLine) {
            (newToken as MultiLineToken).trailingWhitespace = trimmedWhitespace;
        } else {
            (newToken as SingleLineToken).hasSpaceAfter =
                trimmedWhitespace.length > 0;
            newToken.text += trimmedWhitespace.slice(
                0,
                trimmedWhitespace.length - 1,
            );
        }

        middleTokens.push(newToken as Token);
    }

    // Check if there is no whitespace between the last prefix token and the first middle token. If
    // there is no whitespace between them, merge them into one token. We treat the new token as
    // a new token, so any extra fields from the original tokens are not maintained.
    if (prefixTokens.length > 0 && middleTokens.length > 0) {
        // We reassign this variable below, but ESLint doesn't realize that, hence the ignore rule
        // eslint-disable-next-line prefer-const
        let lastPrefixToken = prefixTokens[prefixTokens.length - 1];
        const firstMiddleToken = middleTokens[0];

        if (
            supportsMultiLine &&
            (lastPrefixToken as MultiLineToken).trailingWhitespace.length === 0
        ) {
            // Since we treat this as a new token, we create a new object to ensure any extra
            // fields from the original token are not retained
            (lastPrefixToken as MultiLineToken) = {
                text: lastPrefixToken.text + firstMiddleToken.text,
                trailingWhitespace: (firstMiddleToken as MultiLineToken)
                    .trailingWhitespace,
            };
            prefixTokens[prefixTokens.length - 1] = lastPrefixToken;
            middleTokens.shift();
        } else if (
            !supportsMultiLine &&
            !(lastPrefixToken as SingleLineToken).hasSpaceAfter
        ) {
            // Since we treat this as a new token, we create a new object to ensure any extra
            // fields from the original token are not retained
            (lastPrefixToken as SingleLineToken) = {
                text: lastPrefixToken.text + firstMiddleToken.text,
                hasSpaceAfter: (firstMiddleToken as SingleLineToken)
                    .hasSpaceAfter,
            };
            prefixTokens[prefixTokens.length - 1] = lastPrefixToken;
            middleTokens.shift();
        }
    }

    // Check if there is no whitespace between the last middle token and the first suffix token. If
    // there is no whitespace between them, merge them into one token. We treat the new token as
    // a new token, so any extra fields from the original tokens are not maintained.
    if (middleTokens.length > 0 && suffixTokens.length > 0) {
        const lastMiddleToken = middleTokens[middleTokens.length - 1];
        // We reassign this variable below, but ESLint doesn't realize that, hence the ignore rule
        // eslint-disable-next-line prefer-const
        let firstSuffixToken = suffixTokens[0];

        if (
            supportsMultiLine &&
            (lastMiddleToken as MultiLineToken).trailingWhitespace.length === 0
        ) {
            // Since we treat this as a new token, we create a new object to ensure any extra
            // fields from the original token are not retained
            (firstSuffixToken as MultiLineToken) = {
                text: lastMiddleToken.text + firstSuffixToken.text,
                trailingWhitespace: (firstSuffixToken as MultiLineToken)
                    .trailingWhitespace,
            };
            middleTokens.pop();
            suffixTokens[0] = firstSuffixToken;
        } else if (
            !supportsMultiLine &&
            !(lastMiddleToken as SingleLineToken).hasSpaceAfter
        ) {
            // Since we treat this as a new token, we create a new object to ensure any extra
            // fields from the original token are not retained
            (firstSuffixToken as SingleLineToken) = {
                text: lastMiddleToken.text + firstSuffixToken.text,
                hasSpaceAfter: (firstSuffixToken as SingleLineToken)
                    .hasSpaceAfter,
            };
            middleTokens.pop();
            suffixTokens[0] = firstSuffixToken;
        }
    }

    // Combine the sets of tokens (matched tokens should be in the same ordering as in oldTokens)
    return [...prefixTokens, ...middleTokens, ...suffixTokens];
}
